import React, { useMemo } from 'react';
import { Box, Tooltip, CircularProgress, Typography } from '@mui/material';
import { 
  Wifi as MqttIcon,
  Hub as WebSocketIcon,
  Storage as DatabaseIcon,
  Api as ApiIcon
} from '@mui/icons-material';
import { useHubHealthStore } from '../../store/hubHealthStore';

const HubHealthStatus: React.FC = () => {
  // Use Zustand selectors for only the needed fields
  const healthIndicator = useHubHealthStore(state => state.healthIndicator);
  const isLoading = useHubHealthStore(state => state.isLoading);
  // Only use lastCheck if you display it
  // const lastCheck = useHubHealthStore(state => state.lastCheck);

  const services = [
    { key: 'mqtt', icon: MqttIcon },
    { key: 'websocket', icon: WebSocketIcon },
    { key: 'database', icon: DatabaseIcon },
    { key: 'api', icon: ApiIcon }
  ] as const;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
      case 'running':
      case 'healthy':
        return '#4caf50'; // Green
      case 'disconnected':
      case 'stopped':
      case 'unhealthy':
      case 'down':
        return '#f44336'; // Red
      default:
        return '#ff9800'; // Orange
    }
  };

  const getStatusText = (service: string, status: string) => {
    const statusMap: Record<string, Record<string, string>> = {
      mqtt: {
        connected: 'MQTT Connected',
        disconnected: 'MQTT Disconnected'
      },
      websocket: {
        running: 'WebSocket Running',
        stopped: 'WebSocket Stopped'
      },
      database: {
        healthy: 'Database Healthy',
        unhealthy: 'Database Issues'
      },
      api: {
        running: 'API Server Running',
        down: 'API Server Down'
      }
    };
    return statusMap[service]?.[status] || `${service}: ${status}`;
  };

  const [showSpinner, setShowSpinner] = React.useState(false);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isLoading) {
      timeout = setTimeout(() => setShowSpinner(true), 1000); // Only show spinner if loading > 1s
    } else {
      setShowSpinner(false);
      if (timeout) clearTimeout(timeout);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading]);

  // Memoize the status icon row
  const statusRow = useMemo(() => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {services.map(({ key, icon: Icon }) => {
        const status = healthIndicator?.[key];
        const color = getStatusColor(status || '');
        const tooltipText = getStatusText(key, status || '');
        return (
          <Tooltip 
            key={key}
            title={tooltipText}
            arrow
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  boxShadow: '0 0 8px rgba(0,0,0,0.3)'
                }
              }}
            >
              <Icon sx={{ fontSize: 8, color: 'white' }} />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  ), [healthIndicator]);

  if ((showSpinner && isLoading) || !healthIndicator) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        gap: 0.5,
        backgroundColor: '#f5f5f5',
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #e0e0e0',
        minWidth: 120,
      }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', fontWeight: 'bold' }}>
          HUB STATUS
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <span style={{ fontSize: '0.8rem', color: '#666' }}>Checking...</span>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      gap: 0.5,
      backgroundColor: '#f5f5f5',
      padding: '8px 12px',
      borderRadius: '6px',
      border: '1px solid #e0e0e0',
      minWidth: 120,
    }}>
      <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#666', fontWeight: 'bold' }}>
        HUB STATUS
      </Typography>
      {statusRow}
    </Box>
  );
};

export default React.memo(HubHealthStatus); 